import { AppConfig } from '@core/domain/AppConfig.ts';
import {
  invalidateSharedLocalIdb,
  LOCAL_MEDIA_IDB_STORE,
  openSharedLocalIdb,
  runSharedIdbRead,
  runSharedIdbTx,
} from '@core/infrastructure/driven/indexedDb/sharedLocalIdb.ts';

const SQLITE_RECOVERY_BACKUP_KEY_SUFFIX = '.recovery-backup';

function localSqliteKvDataKeys(): string[] {
  const sqliteKey = AppConfig.getLocalSqliteIdbSqliteKey();
  return [sqliteKey, sqliteKey + SQLITE_RECOVERY_BACKUP_KEY_SUFFIX];
}

/** Clears persisted SQLite bytes (and recovery backup) but keeps `media-blobs` (fotos/firmas). */
export async function clearLocalSqliteIdbDataPreserveMedia(): Promise<void> {
  const store = AppConfig.getLocalSqliteIdbStore();
  const keys = localSqliteKvDataKeys();
  await runSharedIdbTx(
    store,
    'readwrite',
    (os) => {
      for (const key of keys) os.delete(key);
    },
    'indexedDB clear failed',
  );
}

/** Deletes the IndexedDB database (SQLite bytes, recovery backups, and media blobs). */
export function deleteLocalSqliteIndexedDb(): Promise<void> {
  invalidateSharedLocalIdb();
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(AppConfig.getLocalSqliteIdbName());
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('indexedDB.deleteDatabase failed'));
    req.onblocked = () => {
      /* another tab may keep DB open; onsuccess might be delayed */
    };
  });
}

/** Reads persisted sql.js bytes from IndexedDB. */
export async function getLocalSqliteIdbBytes(): Promise<Uint8Array | undefined> {
  return getLocalSqliteIdbBytesAtKey(AppConfig.getLocalSqliteIdbSqliteKey());
}

/** Reads bytes from an arbitrary key in the SQLite KV store (e.g. recovery backup). */
export async function getLocalSqliteIdbBytesAtKey(key: string): Promise<Uint8Array | undefined> {
  const store = AppConfig.getLocalSqliteIdbStore();
  return runSharedIdbRead<Uint8Array | undefined>(
    store,
    (os) => os.get(key),
    'indexedDB.get failed',
  );
}

/** Stores bytes into IndexedDB under an arbitrary key of the same store. */
export async function putLocalSqliteIdbBytesAtKey(key: string, data: Uint8Array): Promise<void> {
  const store = AppConfig.getLocalSqliteIdbStore();
  await runSharedIdbTx(
    store,
    'readwrite',
    (os) => {
      os.put(data, key);
    },
    'indexedDB transaction failed',
  );
}

/** @internal test hook — opens shared DB without closing between ops. */
export function openLocalSqliteIdb(): Promise<IDBDatabase> {
  return openSharedLocalIdb();
}

/** @internal test hook */
export { LOCAL_MEDIA_IDB_STORE };
